import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { 
  getAssets, getIncomingAssets, getSentAssets, getAssetEvents,
  requestAssetEdit, updateAsset, removeAssetPhotos,
  getPendingRequests, getMyPendingRequests, reviewPendingRequest, getTrashedAssets, getSoldAssets,
  sellAsset, getChildAssets, linkChildAsset, unlinkAsset, transferWithChildren,
  sendToLocation, reinstateAsset, getAssetPhotos
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Download, Search, Filter, BarChart3, MapPin, Box, AlertTriangle, 
  Trash2, Edit, Clock, Check, X, ArrowDown, ArrowUp, Eye, History,
  DollarSign, Link, Unlink, ChevronDown, ChevronRight, Send, RotateCcw, Image, ArrowRightLeft
} from 'lucide-react';
import { 
  getStatusColor, getConditionColor, getStatusLabel, formatDate, formatDateTime, 
  formatCurrency, exportToCSV, getEventTypeLabel, isAdminOrOps 
} from '../lib/utils';
import PhotoUpload from '../components/PhotoUpload';
import PhotoViewer, { PhotoThumbnails } from '../components/PhotoViewer';
import TrashModal from '../components/TrashModal';

// Status options including OTHER
const STATUS_OPTIONS = [
  { value: 'IN_SERVICE', label: 'In Service' },
  { value: 'IN_REPAIR', label: 'In Repair' },
  { value: 'OTHER', label: 'Other' },
];

// All editable fields for Admin/Ops
const EDITABLE_FIELDS = [
  { key: 'condition', label: 'Condition', type: 'select', options: ['WORKING', 'BROKEN'] },
  { key: 'asset_status', label: 'Status', type: 'select', options: STATUS_OPTIONS.map(s => s.value) },
  { key: 'status_note', label: 'Status Note', type: 'text', showWhen: { asset_status: 'OTHER' } },
  { key: 'notes', label: 'Notes', type: 'textarea' },
  { key: 'category_id', label: 'Category', type: 'category' },
  { key: 'year_bought', label: 'Year Bought', type: 'number' },
  { key: 'purchase_date', label: 'Purchase Date', type: 'date' },
  { key: 'purchase_cost', label: 'Purchase Cost', type: 'currency' },
  { key: 'make', label: 'Make / Brand', type: 'text' },
  { key: 'model', label: 'Model', type: 'text' },
  { key: 'serial_number', label: 'Serial Number', type: 'text' },
];

export default function Reports() {
  const { user, isAdminOrOps: checkAdminOrOps } = useAuth();
  const { locations, categories, selectedLocation, getLocationName, getCategoryName } = useApp();
  
  const [activeTab, setActiveTab] = useState('inventory');
  const [assets, setAssets] = useState([]);
  const [incomingAssets, setIncomingAssets] = useState([]);
  const [sentAssets, setSentAssets] = useState([]);
  const [trashedAssets, setTrashedAssets] = useState([]);
  const [soldAssets, setSoldAssets] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Expanded parent rows
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [childAssetsMap, setChildAssetsMap] = useState({});
  
  // Sell modal state
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellData, setSellData] = useState({ sold_to: '', sale_price: '', sale_date: '', reason: '', photos: [] });
  
  // Reinstate modal state
  const [showReinstateModal, setShowReinstateModal] = useState(false);
  const [reinstateReason, setReinstateReason] = useState('');
  
  // Photo viewer state
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkableAssets, setLinkableAssets] = useState([]);
  const [selectedChildToLink, setSelectedChildToLink] = useState('');
  
  // Transfer with children modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferChildren, setTransferChildren] = useState([]);
  const [selectedChildrenForTransfer, setSelectedChildrenForTransfer] = useState([]);
  const [transferDestination, setTransferDestination] = useState('');
  
  // Relationship filter
  const [filterRelationship, setFilterRelationship] = useState('');
  
  // Filters
  const [filterLocation, setFilterLocation] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {},
    byLocation: {},
    byCategory: {},
  });

  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showChildTransferModal, setShowChildTransferModal] = useState(false);
  const [childTransferData, setChildTransferData] = useState({ child: null, parent: null });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [assetEvents, setAssetEvents] = useState([]);
  
  // Form states
  const [editReason, setEditReason] = useState('');
  const [editChanges, setEditChanges] = useState({});
  const [approvalNotes, setApprovalNotes] = useState('');

  const isUserAdminOrOps = checkAdminOrOps();

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isUserAdminOrOps && selectedLocation) {
        params.location_id = selectedLocation;
      } else if (filterLocation) {
        params.location_id = filterLocation;
      }
      if (filterCategory) params.category_id = filterCategory;
      if (filterStatus) params.status = filterStatus;
      if (filterCondition) params.condition = filterCondition;
      if (filterYear) params.year_bought = parseInt(filterYear);
      
      const res = await getAssets(params);
      setAssets(res.data);
      
      // Calculate stats
      const byStatus = {};
      const byLocation = {};
      const byCategory = {};
      
      res.data.forEach(asset => {
        byStatus[asset.asset_status] = (byStatus[asset.asset_status] || 0) + 1;
        byLocation[asset.current_location_id] = (byLocation[asset.current_location_id] || 0) + 1;
        byCategory[asset.category_id] = (byCategory[asset.category_id] || 0) + 1;
      });
      
      setStats({
        total: res.data.length,
        byStatus,
        byLocation,
        byCategory,
      });
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [filterLocation, filterCategory, filterStatus, filterCondition, filterYear, selectedLocation, isUserAdminOrOps]);

  const loadIncomingAssets = useCallback(async () => {
    if (!selectedLocation) return;
    try {
      const res = await getIncomingAssets(selectedLocation);
      setIncomingAssets(res.data);
    } catch (error) {
      console.error('Failed to load incoming assets:', error);
    }
  }, [selectedLocation]);

  const loadSentAssets = useCallback(async () => {
    if (!selectedLocation) return;
    try {
      const res = await getSentAssets(selectedLocation);
      setSentAssets(res.data);
    } catch (error) {
      console.error('Failed to load sent assets:', error);
    }
  }, [selectedLocation]);

  const loadTrashedAssets = useCallback(async () => {
    if (!isUserAdminOrOps) return;
    try {
      const res = await getTrashedAssets();
      setTrashedAssets(res.data);
    } catch (error) {
      console.error('Failed to load trashed assets:', error);
    }
  }, [isUserAdminOrOps]);

  const loadSoldAssets = useCallback(async () => {
    try {
      const params = {};
      // Managers can only see sold assets from their locations
      if (!isUserAdminOrOps && selectedLocation) {
        params.location_id = selectedLocation;
      }
      const res = await getSoldAssets(params);
      setSoldAssets(res.data);
    } catch (error) {
      console.error('Failed to load sold assets:', error);
    }
  }, [isUserAdminOrOps, selectedLocation]);

  const loadPendingRequests = useCallback(async () => {
    try {
      if (isUserAdminOrOps) {
        // Admin/Ops see all pending requests
        const res = await getPendingRequests();
        setPendingRequests(res.data);
      } else {
        // Managers see their own pending requests
        const res = await getMyPendingRequests();
        setPendingRequests(res.data);
      }
    } catch (error) {
      console.error('Failed to load pending requests:', error);
    }
  }, [isUserAdminOrOps]);

  useEffect(() => {
    loadAssets();
    loadIncomingAssets();
    loadSentAssets();
    loadTrashedAssets();
    loadSoldAssets();
    loadPendingRequests();
    
    // Auto-refresh pending requests every 30 seconds
    const refreshInterval = setInterval(() => {
      loadPendingRequests();
      loadAssets(); // Also refresh assets to get updated status
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, [loadAssets, loadIncomingAssets, loadSentAssets, loadTrashedAssets, loadSoldAssets, loadPendingRequests]);

  const filteredAssets = assets.filter(asset => {
    // Hide child assets from main list - they're shown under their parent
    // Unless user specifically filters for CHILD assets
    if (asset.relationship_type === 'CHILD' && asset.parent_asset_id && filterRelationship !== 'CHILD') return false;
    if (showPendingOnly && !asset.pending_request_id) return false;
    if (filterRelationship && asset.relationship_type !== filterRelationship) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      asset.barcode.toLowerCase().includes(searchLower) ||
      (asset.notes && asset.notes.toLowerCase().includes(searchLower)) ||
      (asset.make && asset.make.toLowerCase().includes(searchLower)) ||
      (asset.model && asset.model.toLowerCase().includes(searchLower)) ||
      (asset.serial_number && asset.serial_number.toLowerCase().includes(searchLower))
    );
  });

  const handleViewAsset = async (asset) => {
    setSelectedAsset(asset);
    setShowDetailModal(true);
  };

  const handleViewHistory = async (asset) => {
    setSelectedAsset(asset);
    try {
      const res = await getAssetEvents(asset.id);
      setAssetEvents(res.data);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to load asset history');
    }
  };

  // Handler when trash modal succeeds
  const handleTrashSuccess = () => {
    loadAssets();
    loadTrashedAssets();
    loadPendingRequests();
  };

  const handleRequestEdit = async () => {
    // Only require reason for non-Admin/Ops users
    if (!isUserAdminOrOps && !editReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      // Handle photo removal first if any photos are marked for removal
      if (editChanges.photosToRemove && editChanges.photosToRemove.length > 0) {
        const removalReason = isUserAdminOrOps ? 'Direct removal by Admin/Ops' : editReason;
        await removeAssetPhotos(selectedAsset.id, {
          photo_urls: editChanges.photosToRemove,
          reason: removalReason
        });
        
        if (isUserAdminOrOps) {
          toast.success(`${editChanges.photosToRemove.length} photo(s) removed`);
        } else {
          toast.success('Photo removal request submitted for approval');
        }
      }
      
      // Handle other edit changes (excluding photosToRemove from the changes)
      const { photosToRemove, ...otherChanges } = editChanges;
      const hasOtherChanges = Object.keys(otherChanges).some(key => 
        otherChanges[key] !== undefined && otherChanges[key] !== ''
      );
      
      if (hasOtherChanges) {
        if (isUserAdminOrOps) {
          await updateAsset(selectedAsset.id, otherChanges);
          toast.success('Asset updated');
        } else {
          await requestAssetEdit(selectedAsset.id, { reason: editReason, requested_changes: otherChanges });
          toast.success('Edit request submitted for approval');
        }
      }
      
      setShowEditModal(false);
      setEditReason('');
      setEditChanges({});
      loadAssets();
      loadPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process edit');
    }
  };

  const handleReviewRequest = async (approved) => {
    try {
      await reviewPendingRequest(selectedRequest.id, { approved, notes: approvalNotes });
      toast.success(`Request ${approved ? 'approved' : 'denied'}`);
      setShowApprovalModal(false);
      setApprovalNotes('');
      loadAssets();
      loadPendingRequests();
    } catch (error) {
      toast.error('Failed to review request');
    }
  };

  const handleExport = () => {
    const exportData = filteredAssets.map(asset => ({
      Barcode: asset.barcode,
      Category: getCategoryName(asset.category_id),
      'Year Bought': asset.year_bought,
      'Purchase Date': formatDate(asset.purchase_date),
      'Purchase Cost': asset.purchase_cost,
      Condition: asset.condition,
      Status: getStatusLabel(asset.asset_status),
      'Current Location': getLocationName(asset.current_location_id),
      Notes: asset.notes || '',
      'First Received': formatDate(asset.first_received_date),
      'Last Updated': formatDate(asset.updated_at),
    }));
    
    exportToCSV(exportData, `assets-report-${new Date().toISOString().split('T')[0]}`);
    toast.success('Report exported!');
  };

  const clearFilters = () => {
    setFilterLocation('');
    setFilterCategory('');
    setFilterStatus('');
    setFilterCondition('');
    setFilterYear('');
    setSearch('');
    setShowPendingOnly(false);
  };

  // Handle selling an asset
  const handleSellAsset = async () => {
    if (!sellData.sold_to.trim()) {
      toast.error('Please enter buyer name');
      return;
    }
    if (!sellData.sale_price || parseFloat(sellData.sale_price) <= 0) {
      toast.error('Please enter a valid sale price');
      return;
    }
    if (!sellData.photos || sellData.photos.length === 0) {
      toast.error('Please add at least one photo documenting the asset condition');
      return;
    }
    
    try {
      await sellAsset(selectedAsset.id, {
        sold_to: sellData.sold_to,
        sale_price: parseFloat(sellData.sale_price),
        sale_date: sellData.sale_date || undefined,
        reason: sellData.reason || undefined,
        photos: sellData.photos
      });
      toast.success(isUserAdminOrOps ? 'Asset sold successfully!' : 'Sale request submitted for approval');
      setShowSellModal(false);
      setSellData({ sold_to: '', sale_price: '', sale_date: '', reason: '', photos: [] });
      loadAssets();
      loadSoldAssets();
      loadPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sell asset');
    }
  };

  const openSellModal = (asset) => {
    setSelectedAsset(asset);
    setSellData({ 
      sold_to: '', 
      sale_price: '', 
      sale_date: new Date().toISOString().split('T')[0],
      reason: '',
      photos: []
    });
    setShowSellModal(true);
  };

  // Handle reinstating a trashed asset
  const handleReinstateAsset = async () => {
    if (!selectedAsset) return;
    
    try {
      await reinstateAsset(selectedAsset.id, reinstateReason);
      toast.success(isUserAdminOrOps ? 'Asset reinstated successfully!' : 'Reinstate request submitted for approval');
      setShowReinstateModal(false);
      setReinstateReason('');
      setSelectedAsset(null);
      loadTrashedAssets();
      loadAssets();
      loadPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reinstate asset');
    }
  };

  const openReinstateModal = (asset) => {
    setSelectedAsset(asset);
    setReinstateReason('');
    setShowReinstateModal(true);
  };

  // View photos for an asset
  const handleViewPhotos = (photos) => {
    if (!photos || photos.length === 0) {
      toast.info('No photos available for this asset');
      return;
    }
    setViewerPhotos(photos);
    setShowPhotoViewer(true);
  };

  // Handle expanding parent assets to show children
  const toggleParentExpand = async (parentId) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parentId)) {
      newExpanded.delete(parentId);
    } else {
      newExpanded.add(parentId);
      // Load children if not already loaded
      if (!childAssetsMap[parentId]) {
        try {
          const res = await getChildAssets(parentId);
          setChildAssetsMap(prev => ({ ...prev, [parentId]: res.data }));
        } catch (error) {
          console.error('Failed to load children:', error);
        }
      }
    }
    setExpandedParents(newExpanded);
  };

  // Handle unlinking a child asset
  const handleUnlinkAsset = async (assetId, parentId) => {
    if (!window.confirm('Are you sure you want to unlink this asset from its parent?')) return;
    try {
      await unlinkAsset(assetId, 'Unlinked via Reports');
      toast.success(isUserAdminOrOps ? 'Asset unlinked!' : 'Unlink request submitted');
      // Refresh the children list for this parent
      if (parentId && childAssetsMap[parentId]) {
        const res = await getChildAssets(parentId);
        setChildAssetsMap(prev => ({ ...prev, [parentId]: res.data }));
      }
      loadAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink asset');
    }
  };

  // Open link modal to link an existing asset as child
  const openLinkModal = async (parentAsset) => {
    setSelectedAsset(parentAsset);
    try {
      // Get all standalone assets at the same location that could be linked
      const res = await getAssets({ 
        location_id: parentAsset.current_location_id
      });
      // Filter to only standalone assets (not already linked and not parents)
      // Treat missing/undefined relationship_type as STANDALONE (for legacy data)
      const linkable = res.data.filter(a => 
        a.id !== parentAsset.id && 
        (!a.relationship_type || a.relationship_type === 'STANDALONE') &&
        !a.is_removed &&
        a.asset_status !== 'TRASHED' &&
        a.asset_status !== 'SOLD'
      );
      setLinkableAssets(linkable);
      setSelectedChildToLink('');
      setShowLinkModal(true);
    } catch (error) {
      toast.error('Failed to load assets');
    }
  };

  // Handle linking an asset as child
  const handleLinkChild = async () => {
    if (!selectedChildToLink) {
      toast.error('Please select an asset to link');
      return;
    }
    try {
      await linkChildAsset(selectedAsset.id, selectedChildToLink, 'Linked via Reports');
      toast.success(isUserAdminOrOps ? 'Asset linked successfully!' : 'Link request submitted for approval');
      setShowLinkModal(false);
      // Refresh children for this parent
      const res = await getChildAssets(selectedAsset.id);
      setChildAssetsMap(prev => ({ ...prev, [selectedAsset.id]: res.data }));
      loadAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link asset');
    }
  };

  // Open transfer modal for parent with children
  const openTransferModal = async (parentAsset) => {
    setSelectedAsset(parentAsset);
    try {
      const res = await getChildAssets(parentAsset.id);
      setTransferChildren(res.data);
      // By default, select all children for transfer
      setSelectedChildrenForTransfer(res.data.map(c => c.id));
      setTransferDestination('');
      setShowTransferModal(true);
    } catch (error) {
      toast.error('Failed to load children');
    }
  };

  // Handle transfer with children
  const handleTransferWithChildren = async () => {
    if (!transferDestination) {
      toast.error('Please select a destination');
      return;
    }
    try {
      const result = await transferWithChildren(selectedAsset.id, {
        to_location_id: transferDestination,
        child_asset_ids: selectedChildrenForTransfer
      });
      
      const unlinkedCount = result.data.children_unlinked?.length || 0;
      const transferredCount = result.data.children_transferred || 0;
      
      let message = `Parent asset transferred.`;
      if (transferredCount > 0) message += ` ${transferredCount} children transferred with it.`;
      if (unlinkedCount > 0) message += ` ${unlinkedCount} children unlinked and stayed at original location.`;
      
      toast.success(message);
      setShowTransferModal(false);
      loadAssets();
      loadSentAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer');
    }
  };

  // Toggle child selection for transfer
  const toggleChildForTransfer = (childId) => {
    setSelectedChildrenForTransfer(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const hasPendingRequest = (asset) => {
    return asset.pending_request_id || 
           asset.asset_status === 'PENDING_REMOVAL' || 
           asset.asset_status === 'PENDING_EDIT';
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Assets</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.total}</p>
              </div>
              <Box className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Service</p>
                <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stats.byStatus['IN_SERVICE'] || 0}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Transfer</p>
                <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stats.byStatus['IN_TRANSFER'] || 0}
                </p>
              </div>
              <ArrowUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {pendingRequests.length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isUserAdminOrOps ? 'grid-cols-6' : 'grid-cols-4'}`}>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Box className="h-4 w-4 mr-2" />
            Inventory
          </TabsTrigger>
          <TabsTrigger value="incoming" data-testid="tab-incoming">
            <ArrowDown className="h-4 w-4 mr-2" />
            Incoming ({incomingAssets.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">
            <ArrowUp className="h-4 w-4 mr-2" />
            Sent
          </TabsTrigger>
          {isUserAdminOrOps && (
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="h-4 w-4 mr-2" />
              Pending ({pendingRequests.length})
            </TabsTrigger>
          )}
          {isUserAdminOrOps && (
            <TabsTrigger value="trashed" data-testid="tab-trashed">
              <Trash2 className="h-4 w-4 mr-2" />
              Trashed ({trashedAssets.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="sold" data-testid="tab-sold">
            <DollarSign className="h-4 w-4 mr-2" />
            Sold ({soldAssets.length})
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Filter className="h-5 w-5" />
                  Filters
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  Clear All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {isUserAdminOrOps && (
                  <div>
                    <Label className="text-xs">Location</Label>
                    <Select value={filterLocation || "all"} onValueChange={(v) => setFilterLocation(v === "all" ? "" : v)}>
                      <SelectTrigger data-testid="filter-location">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="IN_SERVICE">In Service</SelectItem>
                      <SelectItem value="IN_REPAIR">In Repair</SelectItem>
                      <SelectItem value="IN_TRANSFER">In Transfer</SelectItem>
                      <SelectItem value="OTHER">Other</SelectItem>
                      <SelectItem value="PENDING_REMOVAL">Pending Removal</SelectItem>
                      <SelectItem value="TRASHED">Trashed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Condition</Label>
                  <Select value={filterCondition || "all"} onValueChange={(v) => setFilterCondition(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-condition">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="WORKING">Working</SelectItem>
                      <SelectItem value="BROKEN">Broken</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Year</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 2023"
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    data-testid="filter-year"
                  />
                </div>
                
                <div className="flex items-end">
                  <Button
                    variant={showPendingOnly ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowPendingOnly(!showPendingOnly)}
                    className={showPendingOnly ? 'bg-amber-500 hover:bg-amber-600' : ''}
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Pending Only
                  </Button>
                </div>
                
                {/* Relationship Type Filter */}
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={filterRelationship || "all"} onValueChange={(v) => setFilterRelationship(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filter-relationship">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="STANDALONE">Standalone</SelectItem>
                      <SelectItem value="PARENT">Parents Only</SelectItem>
                      <SelectItem value="CHILD">Children Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search and Export */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by barcode or notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="report-search"
              />
            </div>
            <Button onClick={handleExport} className="bg-purple-600 hover:bg-purple-700" data-testid="export-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="hidden md:table-cell">Type</TableHead>
                      <TableHead className="hidden lg:table-cell">Cost</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden sm:table-cell">Location</TableHead>
                      <TableHead className="hidden md:table-cell">Photos</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : filteredAssets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                          No assets found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAssets.map((asset) => (
                        <React.Fragment key={asset.id}>
                          {/* Main Asset Row */}
                          <TableRow data-testid={`asset-row-${asset.barcode}`} className={asset.relationship_type === 'CHILD' ? 'bg-gray-50/50' : ''}>
                            {/* Expand button for parents */}
                            <TableCell className="w-[40px] p-2">
                              {asset.relationship_type === 'PARENT' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => toggleParentExpand(asset.id)}
                                  data-testid={`expand-${asset.barcode}`}
                                >
                                  {expandedParents.has(asset.id) ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell className="font-mono font-medium">
                              <div className="flex items-center gap-2">
                                {asset.barcode}
                                {hasPendingRequest(asset) && (
                                  <Clock className="h-4 w-4 text-amber-500" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getCategoryName(asset.category_id)}</TableCell>
                            <TableCell className="hidden md:table-cell">
                              <Badge variant="outline" className={
                                asset.relationship_type === 'PARENT' ? 'border-blue-500 text-blue-600' :
                                asset.relationship_type === 'CHILD' ? 'border-purple-500 text-purple-600' :
                                'border-gray-300 text-gray-500'
                              }>
                                {asset.relationship_type === 'PARENT' ? 'Parent' : 
                                 asset.relationship_type === 'CHILD' ? 'Child' : 'Standalone'}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">{formatCurrency(asset.purchase_cost)}</TableCell>
                            <TableCell>
                              <Badge className={getConditionColor(asset.condition)}>
                                {asset.condition}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(asset.asset_status)}>
                                {getStatusLabel(asset.asset_status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {getLocationName(asset.current_location_id)}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {asset.photos && asset.photos.length > 0 ? (
                                <PhotoThumbnails 
                                  photos={asset.photos} 
                                  maxShow={2}
                                  onViewAll={() => handleViewPhotos(asset.photos)}
                                />
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleViewAsset(asset)} data-testid={`view-${asset.barcode}`}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleViewHistory(asset)} data-testid={`history-${asset.barcode}`}>
                                  <History className="h-4 w-4" />
                                </Button>
                                {asset.asset_status !== 'TRASHED' && asset.asset_status !== 'SOLD' && !asset.is_removed && (
                                  <>
                                    {/* Link button for Parents */}
                                    {asset.relationship_type === 'PARENT' && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => openLinkModal(asset)}
                                        data-testid={`link-${asset.barcode}`}
                                        title="Link child asset"
                                      >
                                        <Link className="h-4 w-4 text-blue-500" />
                                      </Button>
                                    )}
                                    {/* Unlink button for Children */}
                                    {asset.relationship_type === 'CHILD' && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleUnlinkAsset(asset.id, asset.parent_asset_id)}
                                        data-testid={`unlink-${asset.barcode}`}
                                        title="Unlink from parent"
                                      >
                                        <Unlink className="h-4 w-4 text-purple-500" />
                                      </Button>
                                    )}
                                    {/* Transfer button for Parents (shows children selector) */}
                                    {asset.relationship_type === 'PARENT' && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => openTransferModal(asset)}
                                        data-testid={`transfer-${asset.barcode}`}
                                        title="Transfer with children"
                                      >
                                        <Send className="h-4 w-4 text-green-500" />
                                      </Button>
                                    )}
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      onClick={() => {
                                        setSelectedAsset(asset);
                                        setEditChanges({ condition: asset.condition, asset_status: asset.asset_status });
                                        setShowEditModal(true);
                                      }}
                                      data-testid={`edit-${asset.barcode}`}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      onClick={() => openSellModal(asset)}
                                      data-testid={`sell-${asset.barcode}`}
                                      title="Sell Asset"
                                    >
                                      <DollarSign className="h-4 w-4 text-cyan-500" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      onClick={() => {
                                        setSelectedAsset({...asset, category_name: getCategoryName(asset.category_id)});
                                        setShowTrashModal(true);
                                      }}
                                      data-testid={`remove-${asset.barcode}`}
                                      title="Trash Asset"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Children Rows */}
                          {asset.relationship_type === 'PARENT' && expandedParents.has(asset.id) && (
                            <>
                              {childAssetsMap[asset.id]?.length > 0 ? (
                                childAssetsMap[asset.id].map((child) => (
                                  <TableRow key={child.id} className="bg-blue-50/50 border-l-4 border-l-blue-300">
                                    <TableCell className="w-[40px]"></TableCell>
                                    <TableCell className="font-mono">
                                      <div className="flex items-center gap-2 pl-4">
                                        <span className="text-blue-400">└</span>
                                        {child.barcode}
                                      </div>
                                    </TableCell>
                                    <TableCell>{getCategoryName(child.category_id)}</TableCell>
                                    <TableCell className="hidden md:table-cell">
                                      <Badge variant="outline" className="border-purple-500 text-purple-600 text-xs">Child</Badge>
                                    </TableCell>
                                    <TableCell className="hidden lg:table-cell">{formatCurrency(child.purchase_cost)}</TableCell>
                                    <TableCell>
                                      <Badge className={getConditionColor(child.condition)}>{child.condition}</Badge>
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={getStatusColor(child.asset_status)}>{getStatusLabel(child.asset_status)}</Badge>
                                    </TableCell>
                                    <TableCell className="hidden sm:table-cell">{getLocationName(child.current_location_id)}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        {/* View Details */}
                                        <Button variant="ghost" size="icon" onClick={() => handleViewAsset(child)} title="View Details">
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        {/* History */}
                                        <Button variant="ghost" size="icon" onClick={() => handleViewHistory(child)} title="View History">
                                          <History className="h-4 w-4" />
                                        </Button>
                                        {/* Edit */}
                                        <Button 
                                          variant="ghost" 
                                          size="icon" 
                                          onClick={() => {
                                            setSelectedAsset({...child, category_name: getCategoryName(child.category_id)});
                                            setShowEditModal(true);
                                          }}
                                          title="Edit Asset"
                                          data-testid={`edit-child-${child.barcode}`}
                                        >
                                          <Edit className="h-4 w-4 text-blue-500" />
                                        </Button>
                                        {/* Transfer - with special child transfer modal */}
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => {
                                            setChildTransferData({ child: {...child, category_name: getCategoryName(child.category_id)}, parent: asset });
                                            setShowChildTransferModal(true);
                                          }}
                                          title="Transfer Asset"
                                          data-testid={`transfer-child-${child.barcode}`}
                                        >
                                          <ArrowRightLeft className="h-4 w-4 text-green-500" />
                                        </Button>
                                        {/* Sell */}
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => {
                                            setSelectedAsset({...child, category_name: getCategoryName(child.category_id)});
                                            setShowSellModal(true);
                                          }}
                                          title="Sell Asset"
                                          data-testid={`sell-child-${child.barcode}`}
                                        >
                                          <DollarSign className="h-4 w-4 text-emerald-500" />
                                        </Button>
                                        {/* Trash */}
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => {
                                            setSelectedAsset({...child, category_name: getCategoryName(child.category_id)});
                                            setShowTrashModal(true);
                                          }}
                                          title="Trash Asset"
                                          data-testid={`trash-child-${child.barcode}`}
                                        >
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                        {/* Unlink */}
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={() => handleUnlinkAsset(child.id, asset.id)}
                                          title="Unlink from parent"
                                          data-testid={`unlink-child-${child.barcode}`}
                                        >
                                          <Unlink className="h-4 w-4 text-purple-500" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow className="bg-gray-50">
                                  <TableCell colSpan={9} className="text-center py-4 text-gray-400 text-sm italic">
                                    No children linked to this parent
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incoming Tab */}
        <TabsContent value="incoming" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Incoming Assets</CardTitle>
              <p className="text-sm text-gray-500">Assets being transferred to your location</p>
            </CardHeader>
            <CardContent>
              {incomingAssets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No incoming assets
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>From Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incomingAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell className="font-mono font-medium">{asset.barcode}</TableCell>
                        <TableCell>{getCategoryName(asset.category_id)}</TableCell>
                        <TableCell>{getLocationName(asset.current_location_id)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(asset.asset_status)}>
                            {getStatusLabel(asset.asset_status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            className="bg-blue-500 hover:bg-blue-600"
                            onClick={() => {
                              window.location.href = `/?receive=${asset.barcode}`;
                            }}
                          >
                            Mark as Received
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sent Tab */}
        <TabsContent value="sent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sent Assets</CardTitle>
              <p className="text-sm text-gray-500">Assets you have sent to other locations</p>
            </CardHeader>
            <CardContent>
              {sentAssets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No sent assets
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Sent To</TableHead>
                      <TableHead>Date Sent</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sentAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell className="font-mono font-medium">{asset.barcode}</TableCell>
                        <TableCell>{getCategoryName(asset.category_id)}</TableCell>
                        <TableCell>{getLocationName(asset.sent_to_location_id)}</TableCell>
                        <TableCell>{formatDate(asset.sent_date)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(asset.asset_status)}>
                            {getStatusLabel(asset.asset_status)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Requests Tab (Admin/Ops only) */}
        {isUserAdminOrOps && (
          <TabsContent value="pending" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending Approval Requests</CardTitle>
                <p className="text-sm text-gray-500">Review and approve or deny requests from managers</p>
              </CardHeader>
              <CardContent>
                {pendingRequests.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No pending requests
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Requested By</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingRequests.map((req) => (
                        <TableRow key={req.id}>
                          <TableCell className="font-mono font-medium">
                            {req.asset?.barcode || 'Unknown'}
                          </TableCell>
                          <TableCell>
                            <Badge className={req.request_type === 'REMOVAL' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                              {req.request_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{req.requester?.name || 'Unknown'}</TableCell>
                          <TableCell className="max-w-xs truncate">{req.reason}</TableCell>
                          <TableCell>{formatDate(req.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700 mr-2"
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowApprovalModal(true);
                              }}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Trashed Assets Tab (Admin/Ops only) */}
        {isUserAdminOrOps && (
          <TabsContent value="trashed" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-500" />
                  Trashed / Removed Assets
                </CardTitle>
                <p className="text-sm text-gray-500">Assets that have been removed from active inventory</p>
              </CardHeader>
              <CardContent>
                {trashedAssets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No trashed assets
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Barcode</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Make/Model</TableHead>
                        <TableHead>Last Location</TableHead>
                        <TableHead>Photos</TableHead>
                        <TableHead>Removed Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trashedAssets.map((asset) => (
                        <TableRow key={asset.id} className="bg-red-50/50">
                          <TableCell className="font-mono font-medium">{asset.barcode}</TableCell>
                          <TableCell>{asset.category_name || getCategoryName(asset.category_id)}</TableCell>
                          <TableCell>
                            {asset.make || asset.model ? (
                              <span>{asset.make} {asset.model}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>{asset.location_name || getLocationName(asset.current_location_id)}</TableCell>
                          <TableCell>
                            {asset.photos && asset.photos.length > 0 ? (
                              <PhotoThumbnails 
                                photos={asset.photos} 
                                onViewAll={() => handleViewPhotos(asset.photos)}
                              />
                            ) : (
                              <span className="text-gray-400 text-sm">None</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(asset.updated_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleViewAsset(asset)} data-testid={`view-trashed-${asset.barcode}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleViewHistory(asset)} data-testid={`history-trashed-${asset.barcode}`}>
                                <History className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openReinstateModal(asset)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Reinstate Asset"
                                data-testid={`reinstate-${asset.barcode}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Sold Assets Tab */}
        <TabsContent value="sold" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-cyan-500" />
                Sold Assets
              </CardTitle>
              <p className="text-sm text-gray-500">Assets that have been sold and removed from active inventory</p>
            </CardHeader>
            <CardContent>
              {soldAssets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No sold assets
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Make/Model</TableHead>
                      <TableHead>Sold To</TableHead>
                      <TableHead>Sale Price</TableHead>
                      <TableHead>Photos</TableHead>
                      <TableHead>Sale Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {soldAssets.map((asset) => (
                      <TableRow key={asset.id} className="bg-cyan-50/50">
                        <TableCell className="font-mono font-medium">{asset.barcode}</TableCell>
                        <TableCell>{asset.category_name || getCategoryName(asset.category_id)}</TableCell>
                        <TableCell>
                          {asset.make || asset.model ? (
                            <span>{asset.make} {asset.model}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{asset.sold_to || '-'}</TableCell>
                        <TableCell className="font-medium text-green-600">{formatCurrency(asset.sale_price)}</TableCell>
                        <TableCell>
                          {asset.photos && asset.photos.length > 0 ? (
                            <PhotoThumbnails 
                              photos={asset.photos} 
                              maxShow={2}
                              onViewAll={() => handleViewPhotos(asset.photos)}
                            />
                          ) : (
                            <span className="text-gray-400 text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(asset.sale_date)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleViewAsset(asset)} data-testid={`view-sold-${asset.barcode}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleViewHistory(asset)} data-testid={`history-sold-${asset.barcode}`}>
                              <History className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Asset Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Asset Details</DialogTitle>
            <DialogDescription className="sr-only">View complete asset information</DialogDescription>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-mono text-2xl font-bold">{selectedAsset.barcode}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Category:</span> <span className="font-medium">{getCategoryName(selectedAsset.category_id)}</span></div>
                <div><span className="text-gray-500">Year Bought:</span> <span className="font-medium">{selectedAsset.year_bought}</span></div>
                {selectedAsset.make && (
                  <div><span className="text-gray-500">Make:</span> <span className="font-medium">{selectedAsset.make}</span></div>
                )}
                {selectedAsset.model && (
                  <div><span className="text-gray-500">Model:</span> <span className="font-medium">{selectedAsset.model}</span></div>
                )}
                {selectedAsset.serial_number && (
                  <div className="col-span-2"><span className="text-gray-500">Serial Number:</span> <span className="font-medium font-mono">{selectedAsset.serial_number}</span></div>
                )}
                <div><span className="text-gray-500">Purchase Date:</span> <span className="font-medium">{formatDate(selectedAsset.purchase_date)}</span></div>
                <div><span className="text-gray-500">Purchase Cost:</span> <span className="font-medium">{formatCurrency(selectedAsset.purchase_cost)}</span></div>
                <div><span className="text-gray-500">Condition:</span> <Badge className={getConditionColor(selectedAsset.condition)}>{selectedAsset.condition}</Badge></div>
                <div>
                  <span className="text-gray-500">Status:</span>{' '}
                  <Badge className={getStatusColor(selectedAsset.asset_status)}>{getStatusLabel(selectedAsset.asset_status)}</Badge>
                  {selectedAsset.asset_status === 'OTHER' && selectedAsset.status_note && (
                    <span className="text-xs text-gray-500 ml-1">({selectedAsset.status_note})</span>
                  )}
                </div>
                <div className="col-span-2"><span className="text-gray-500">Location:</span> <span className="font-medium">{getLocationName(selectedAsset.current_location_id)}</span></div>
                <div className="col-span-2"><span className="text-gray-500">First Received:</span> <span className="font-medium">{formatDate(selectedAsset.first_received_date)} at {getLocationName(selectedAsset.first_received_location_id)}</span></div>
                {selectedAsset.notes && (
                  <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="font-medium">{selectedAsset.notes}</span></div>
                )}
              </div>
              {/* Photos Section */}
              {selectedAsset.photos && selectedAsset.photos.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Photos ({selectedAsset.photos.length})</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewPhotos(selectedAsset.photos)}
                    >
                      <Image className="h-4 w-4 mr-1" />
                      View All
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PhotoThumbnails 
                      photos={selectedAsset.photos} 
                      maxShow={4}
                      onViewAll={() => handleViewPhotos(selectedAsset.photos)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Asset History</DialogTitle>
            <DialogDescription>
              {selectedAsset?.barcode} - Complete movement and change history
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            {assetEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No history available</div>
            ) : (
              <div className="space-y-4">
                {assetEvents.map((event, index) => (
                  <div key={event.id} className="relative flex gap-4">
                    {index < assetEvents.length - 1 && (
                      <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200" />
                    )}
                    <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                      <History className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {getEventTypeLabel(event.event_type)}
                        </Badge>
                        <span className="text-xs text-gray-500">{formatDateTime(event.event_date)}</span>
                      </div>
                      {event.from_location_id && (
                        <p className="text-sm text-gray-600">From: {getLocationName(event.from_location_id)}</p>
                      )}
                      {event.to_location_id && (
                        <p className="text-sm text-gray-600">To: {getLocationName(event.to_location_id)}</p>
                      )}
                      {event.reason && (
                        <p className="text-sm text-gray-600">Reason: {event.reason}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Trash Modal - with required photos */}
      <TrashModal
        open={showTrashModal}
        onClose={() => setShowTrashModal(false)}
        asset={selectedAsset}
        onSuccess={handleTrashSuccess}
      />

      {/* Child Transfer Modal - Choose to transfer alone or with parent */}
      <Dialog open={showChildTransferModal} onOpenChange={setShowChildTransferModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-green-500" />
              Transfer Child Asset
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono font-bold">{childTransferData.child?.barcode}</span>
              <span className="text-gray-500 ml-2">is linked to parent </span>
              <span className="font-mono font-bold">{childTransferData.parent?.barcode}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                This asset is currently linked to a parent. Choose how you want to transfer it:
              </p>
            </div>
            
            {/* Option 1: Transfer Alone (Unlink First) */}
            <div 
              className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={async () => {
                // First unlink the child, then they can transfer from main inventory
                try {
                  await unlinkAsset(childTransferData.child.id, 'Unlinked for independent transfer');
                  toast.success(isUserAdminOrOps 
                    ? 'Asset unlinked! It is now standalone and can be transferred independently.' 
                    : 'Unlink request submitted. Once approved, the asset can be transferred.');
                  setShowChildTransferModal(false);
                  // Refresh the children list and assets
                  if (childTransferData.parent.id && childAssetsMap[childTransferData.parent.id]) {
                    const res = await getChildAssets(childTransferData.parent.id);
                    setChildAssetsMap(prev => ({ ...prev, [childTransferData.parent.id]: res.data }));
                  }
                  loadAssets();
                  loadPendingRequests();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to unlink asset');
                }
              }}
              data-testid="transfer-child-alone"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Unlink className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Transfer Child Alone</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Unlink from parent first, then transfer independently.
                    {isUserAdminOrOps 
                      ? ' The child will become a standalone asset immediately.'
                      : ' An unlink request will be submitted for approval.'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Option 2: Transfer With Parent */}
            <div 
              className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => {
                setShowChildTransferModal(false);
                // Show info and guide user to Dashboard for parent transfer
                toast.info(`To transfer with parent, go to Dashboard and select "${childTransferData.parent.barcode}" for transfer. You can choose which children to include.`, {
                  duration: 6000
                });
              }}
              data-testid="transfer-with-parent"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Link className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Transfer With Parent</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Go to the Dashboard and initiate transfer on the parent asset. 
                    You can select which children to transfer together.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChildTransferModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal - Comprehensive version with all fields */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isUserAdminOrOps ? 'Edit Asset' : 'Request Asset Edit'}</DialogTitle>
            <DialogDescription>
              {selectedAsset?.barcode} - {isUserAdminOrOps 
                ? 'Make changes to the asset information.'
                : 'Your edit request will be sent to Admin/Ops for approval.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Condition */}
            <div>
              <Label>Condition</Label>
              <Select 
                value={editChanges.condition || selectedAsset?.condition || ''} 
                onValueChange={(v) => setEditChanges({...editChanges, condition: v})}
              >
                <SelectTrigger data-testid="edit-condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WORKING">Working</SelectItem>
                  <SelectItem value="BROKEN">Broken</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status */}
            <div>
              <Label>Status</Label>
              <Select 
                value={editChanges.asset_status || selectedAsset?.asset_status || ''} 
                onValueChange={(v) => setEditChanges({...editChanges, asset_status: v, status_note: v !== 'OTHER' ? undefined : editChanges.status_note})}
              >
                <SelectTrigger data-testid="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN_SERVICE">In Service</SelectItem>
                  <SelectItem value="IN_REPAIR">In Repair</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Note - only when status is OTHER */}
            {(editChanges.asset_status === 'OTHER' || (!editChanges.asset_status && selectedAsset?.asset_status === 'OTHER')) && (
              <div>
                <Label>Status Note *</Label>
                <Input
                  value={editChanges.status_note || selectedAsset?.status_note || ''}
                  onChange={(e) => setEditChanges({...editChanges, status_note: e.target.value})}
                  placeholder="Describe the status..."
                  data-testid="edit-status-note"
                />
              </div>
            )}
            
            {/* Category */}
            <div>
              <Label>Category</Label>
              <Select 
                value={editChanges.category_id || selectedAsset?.category_id || ''} 
                onValueChange={(v) => setEditChanges({...editChanges, category_id: v})}
              >
                <SelectTrigger data-testid="edit-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Make and Model */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Make / Brand</Label>
                <Input
                  value={editChanges.make !== undefined ? editChanges.make : (selectedAsset?.make || '')}
                  onChange={(e) => setEditChanges({...editChanges, make: e.target.value})}
                  placeholder="e.g., Dell, HP"
                  data-testid="edit-make"
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  value={editChanges.model !== undefined ? editChanges.model : (selectedAsset?.model || '')}
                  onChange={(e) => setEditChanges({...editChanges, model: e.target.value})}
                  placeholder="e.g., XPS 15"
                  data-testid="edit-model"
                />
              </div>
            </div>
            
            {/* Serial Number */}
            <div>
              <Label>Serial Number</Label>
              <Input
                value={editChanges.serial_number !== undefined ? editChanges.serial_number : (selectedAsset?.serial_number || '')}
                onChange={(e) => setEditChanges({...editChanges, serial_number: e.target.value})}
                placeholder="Enter serial number"
                className="font-mono"
                data-testid="edit-serial-number"
              />
            </div>
            
            {/* Year and Purchase Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Year Bought</Label>
                <Input
                  type="number"
                  min="1900"
                  max="2100"
                  value={editChanges.year_bought !== undefined ? editChanges.year_bought : (selectedAsset?.year_bought || '')}
                  onChange={(e) => setEditChanges({...editChanges, year_bought: parseInt(e.target.value) || null})}
                  data-testid="edit-year"
                />
              </div>
              <div>
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={editChanges.purchase_date !== undefined ? editChanges.purchase_date : (selectedAsset?.purchase_date || '')}
                  onChange={(e) => setEditChanges({...editChanges, purchase_date: e.target.value})}
                  data-testid="edit-purchase-date"
                />
              </div>
            </div>
            
            {/* Purchase Cost */}
            <div>
              <Label>Purchase Cost ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={editChanges.purchase_cost !== undefined ? editChanges.purchase_cost : (selectedAsset?.purchase_cost || '')}
                onChange={(e) => setEditChanges({...editChanges, purchase_cost: e.target.value ? parseFloat(e.target.value) : null})}
                placeholder="0.00"
                data-testid="edit-purchase-cost"
              />
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editChanges.notes !== undefined ? editChanges.notes : (selectedAsset?.notes || '')}
                onChange={(e) => setEditChanges({...editChanges, notes: e.target.value})}
                placeholder="Additional notes about the asset..."
                data-testid="edit-notes"
              />
            </div>

            {/* Add Photos - visible to all users */}
            <div className="border-t pt-4">
              <PhotoUpload
                photos={editChanges.photos || []}
                onPhotosChange={(photos) => setEditChanges({...editChanges, photos})}
                required={false}
                entityType="asset"
                entityId={selectedAsset?.id || 'temp'}
                label="Add Photos (Optional)"
                helpText="Add additional documentation photos to this asset"
              />
              {selectedAsset?.photos && selectedAsset.photos.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Existing photos: {selectedAsset.photos.length}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAsset.photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={photo.startsWith('http') ? photo : `${process.env.REACT_APP_BACKEND_URL}${photo}`}
                          alt={`Photo ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded cursor-pointer"
                          onClick={() => handleViewPhotos(selectedAsset.photos)}
                        />
                        <button
                          type="button"
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            const photosToRemove = editChanges.photosToRemove || [];
                            if (!photosToRemove.includes(photo)) {
                              setEditChanges({...editChanges, photosToRemove: [...photosToRemove, photo]});
                            }
                          }}
                          title="Mark for removal"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  {editChanges.photosToRemove && editChanges.photosToRemove.length > 0 && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                      <p className="text-xs font-medium text-red-800 mb-1">
                        {editChanges.photosToRemove.length} photo(s) marked for removal
                      </p>
                      <p className="text-xs text-red-600">
                        {isUserAdminOrOps 
                          ? "Photos will be removed when you save changes."
                          : "A request will be submitted for approval."}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => setEditChanges({...editChanges, photosToRemove: []})}
                      >
                        Clear selection
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Relationship Type - Admin/Ops only */}
            {isUserAdminOrOps && (
              <div className="border-t pt-4">
                <Label>Relationship Type</Label>
                <Select 
                  value={editChanges.relationship_type !== undefined ? editChanges.relationship_type : (selectedAsset?.relationship_type || 'STANDALONE')}
                  onValueChange={(v) => setEditChanges({...editChanges, relationship_type: v, parent_asset_id: v === 'CHILD' ? editChanges.parent_asset_id : null})}
                >
                  <SelectTrigger data-testid="edit-relationship-type">
                    <SelectValue placeholder="Select relationship type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STANDALONE">Standalone</SelectItem>
                    <SelectItem value="PARENT">Parent</SelectItem>
                    <SelectItem value="CHILD">Child</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Parent Selection for Child assets */}
                {(editChanges.relationship_type === 'CHILD' || (editChanges.relationship_type === undefined && selectedAsset?.relationship_type === 'CHILD')) && (
                  <div className="mt-3">
                    <Label>Parent Asset</Label>
                    <Select 
                      value={editChanges.parent_asset_id !== undefined ? editChanges.parent_asset_id : (selectedAsset?.parent_asset_id || '')}
                      onValueChange={(v) => setEditChanges({...editChanges, parent_asset_id: v})}
                    >
                      <SelectTrigger data-testid="edit-parent-asset">
                        <SelectValue placeholder="Select parent asset" />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.filter(a => 
                          a.relationship_type === 'PARENT' && 
                          a.id !== selectedAsset?.id &&
                          a.current_location_id === selectedAsset?.current_location_id &&
                          !a.is_removed &&
                          a.asset_status !== 'TRASHED'
                        ).map((parent) => (
                          <SelectItem key={parent.id} value={parent.id}>
                            {parent.barcode} ({getCategoryName(parent.category_id)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Only parent assets at the same location are shown</p>
                  </div>
                )}
                
                {/* Warning when changing relationship type */}
                {editChanges.relationship_type && editChanges.relationship_type !== selectedAsset?.relationship_type && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                    Changing relationship type will update Asset Details, Asset History, and Audit Log.
                    {selectedAsset?.relationship_type === 'PARENT' && childAssetsMap[selectedAsset.id]?.length > 0 && (
                      <span className="block mt-1 font-medium">⚠️ This parent has {childAssetsMap[selectedAsset.id].length} linked children that will become standalone.</span>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Reason - only for managers */}
            {!isUserAdminOrOps && (
              <div>
                <Label>Reason for changes *</Label>
                <Textarea
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="Why are these changes needed?"
                  data-testid="edit-reason"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleRequestEdit} data-testid="edit-submit-btn">
              {isUserAdminOrOps ? 'Save Changes' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Request</DialogTitle>
            <DialogDescription>
              {selectedRequest?.request_type} request for {selectedRequest?.asset?.barcode}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Requested by</p>
              <p className="font-medium">{selectedRequest?.requester?.name}</p>
              <p className="text-sm text-gray-500 mt-2">Reason</p>
              <p>{selectedRequest?.reason}</p>
              {selectedRequest?.requested_changes && (
                <>
                  <p className="text-sm text-gray-500 mt-2">Requested Changes</p>
                  <pre className="text-sm bg-white p-2 rounded mt-1">
                    {JSON.stringify(selectedRequest.requested_changes, null, 2)}
                  </pre>
                </>
              )}
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about your decision..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleReviewRequest(false)}>
              <X className="h-4 w-4 mr-2" />
              Deny
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleReviewRequest(true)}>
              <Check className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell Asset Modal */}
      <Dialog open={showSellModal} onOpenChange={setShowSellModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-cyan-500" />
              {isUserAdminOrOps ? 'Sell Asset' : 'Request Asset Sale'}
            </DialogTitle>
            <DialogDescription>
              {selectedAsset?.barcode} - {isUserAdminOrOps 
                ? 'Complete the sale information below.' 
                : 'Your sale request will be sent to Admin/Ops for approval.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sold To (Buyer Name/Company) *</Label>
              <Input
                value={sellData.sold_to}
                onChange={(e) => setSellData({...sellData, sold_to: e.target.value})}
                placeholder="Enter buyer name or company"
                data-testid="sell-to-input"
              />
            </div>
            <div>
              <Label>Sale Price ($) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={sellData.sale_price}
                onChange={(e) => setSellData({...sellData, sale_price: e.target.value})}
                placeholder="0.00"
                data-testid="sell-price-input"
              />
            </div>
            <div>
              <Label>Sale Date</Label>
              <Input
                type="date"
                value={sellData.sale_date}
                onChange={(e) => setSellData({...sellData, sale_date: e.target.value})}
                data-testid="sell-date-input"
              />
            </div>
            {!isUserAdminOrOps && (
              <div>
                <Label>Reason / Notes</Label>
                <Textarea
                  value={sellData.reason}
                  onChange={(e) => setSellData({...sellData, reason: e.target.value})}
                  placeholder="Why is this asset being sold?"
                  data-testid="sell-reason-input"
                />
              </div>
            )}
            <PhotoUpload
              photos={sellData.photos}
              onPhotosChange={(photos) => setSellData({...sellData, photos})}
              required={true}
              entityType="sale"
              entityId={selectedAsset?.id || 'temp'}
              label="Sale Photos"
              helpText="Document the asset condition at time of sale"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSellModal(false)}>Cancel</Button>
            <Button 
              className={`${(!sellData.photos || sellData.photos.length === 0) ? 'bg-gray-400 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700'}`}
              onClick={handleSellAsset} 
              disabled={!sellData.photos || sellData.photos.length === 0}
              data-testid="sell-submit-btn"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              {isUserAdminOrOps ? 'Sell Asset' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate Asset Modal */}
      <Dialog open={showReinstateModal} onOpenChange={setShowReinstateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-green-500" />
              {isUserAdminOrOps ? 'Reinstate Asset' : 'Request Reinstatement'}
            </DialogTitle>
            <DialogDescription>
              Reinstate <span className="font-mono font-bold">{selectedAsset?.barcode}</span> back to active inventory
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              This asset will be returned to active inventory with IN_SERVICE status.
            </div>
            <div>
              <Label>Reason for Reinstatement</Label>
              <Textarea
                value={reinstateReason}
                onChange={(e) => setReinstateReason(e.target.value)}
                placeholder="Why is this asset being reinstated?"
                data-testid="reinstate-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReinstateModal(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleReinstateAsset} data-testid="reinstate-submit-btn">
              <RotateCcw className="h-4 w-4 mr-2" />
              {isUserAdminOrOps ? 'Reinstate' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Modal */}
      <PhotoViewer 
        photos={viewerPhotos}
        open={showPhotoViewer}
        onClose={() => setShowPhotoViewer(false)}
        title="Asset Photos"
      />

      {/* Link Child Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-blue-500" />
              Link Child Asset
            </DialogTitle>
            <DialogDescription>
              Select an existing standalone asset to link as a child to {selectedAsset?.barcode}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {linkableAssets.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No standalone assets available at this location to link.
              </div>
            ) : (
              <div>
                <Label>Select Asset to Link</Label>
                <Select value={selectedChildToLink} onValueChange={setSelectedChildToLink}>
                  <SelectTrigger data-testid="link-child-select">
                    <SelectValue placeholder="Select an asset..." />
                  </SelectTrigger>
                  <SelectContent>
                    {linkableAssets.map((asset) => (
                      <SelectItem key={asset.id} value={asset.id}>
                        {asset.barcode} - {getCategoryName(asset.category_id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>Cancel</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleLinkChild}
              disabled={linkableAssets.length === 0 || !selectedChildToLink}
              data-testid="link-submit-btn"
            >
              <Link className="h-4 w-4 mr-2" />
              {isUserAdminOrOps ? 'Link Asset' : 'Request Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer with Children Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-green-500" />
              Transfer Parent Asset
            </DialogTitle>
            <DialogDescription>
              Transfer {selectedAsset?.barcode} and select which children to include.
              Unselected children will be unlinked and stay at the current location.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Destination Location *</Label>
              <Select value={transferDestination} onValueChange={setTransferDestination}>
                <SelectTrigger data-testid="transfer-destination">
                  <SelectValue placeholder="Select destination..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.filter(l => l.id !== selectedAsset?.current_location_id).map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {transferChildren.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Select Children to Transfer</Label>
                  <div className="space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedChildrenForTransfer(transferChildren.map(c => c.id))}
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedChildrenForTransfer([])}
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                  {transferChildren.map((child) => (
                    <div key={child.id} className="flex items-center p-3 hover:bg-gray-50">
                      <Checkbox
                        id={`child-${child.id}`}
                        checked={selectedChildrenForTransfer.includes(child.id)}
                        onCheckedChange={() => toggleChildForTransfer(child.id)}
                      />
                      <label htmlFor={`child-${child.id}`} className="ml-3 flex-1 cursor-pointer">
                        <span className="font-mono font-medium">{child.barcode}</span>
                        <span className="text-gray-500 ml-2">({getCategoryName(child.category_id)})</span>
                      </label>
                      <Badge className={getConditionColor(child.condition)}>{child.condition}</Badge>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedChildrenForTransfer.length} of {transferChildren.length} children will transfer.
                  {transferChildren.length - selectedChildrenForTransfer.length > 0 && (
                    <span className="text-amber-600 ml-1">
                      {transferChildren.length - selectedChildrenForTransfer.length} will be unlinked.
                    </span>
                  )}
                </p>
              </div>
            )}
            
            {transferChildren.length === 0 && (
              <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                This parent has no linked children.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            <Button 
              className="bg-green-600 hover:bg-green-700" 
              onClick={handleTransferWithChildren}
              disabled={!transferDestination}
              data-testid="transfer-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
